// Central SEO configuration for Litigation India.
// Production canonical domain (confirmed with owner).
export const SITE_URL = "https://litigationindia.com";
export const DEFAULT_OG =
  "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=1200&q=80";
export const ORG_NAME = "Litigation India";

// Core search-targeting keywords requested by the owner (used across all pages).
export const BASE_KEYWORDS =
  "lawyer, advocate, Supreme Court lawyer, best Supreme Court lawyer, advocate for Supreme Court, High Court lawyer, High Court advocate, legal updates, legal news India, Litigation India";

// Practice / knowledge areas used across structured data (factual, non-promotional).
export const PRACTICE_AREAS = [
  "Supreme Court of India litigation",
  "Special Leave Petition (SLP)",
  "Bail matters — anticipatory bail, interim bail",
  "Criminal law — murder, PMLA, NDPS, FIR quashing",
  "Constitutional law — Article 32, Article 136 writs",
  "Service law — CAT appeals, promotion, pension",
  "Family and property law — partition, custody, matrimonial transfer",
  "Insolvency — IBC, NCLAT, SARFAESI",
  "International arbitration — Section 11, foreign awards",
];

/**
 * WebSite schema — placed on the home page for Google's Search Box / Sitelinks.
 */
export function webSiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: ORG_NAME,
    description:
      "Litigation India — Supreme Court judgment summaries in plain English & Hindi, and free initial guidance from volunteer Supreme Court advocates.",
    inLanguage: ["en-IN", "hi-IN"],
    potentialAction: {
      "@type": "SearchAction",
      target: { "@type": "EntryPoint", urlTemplate: `${SITE_URL}/judgments?q={search_term_string}` },
      "query-input": "required name=search_term_string",
    },
  };
}

// Per-page meta. Note: `keywords` are search-targeting terms only, not claims.
export const PAGE_SEO = {
  home: {
    title: "Litigation India: Supreme Court Judgments & Free Help",
    description:
      "Litigation India: Insight First, Action Next. Supreme Court judgments explained in English & Hindi, connecting litigants with Supreme Court lawyers.",
    keywords: `Litigation India, ${BASE_KEYWORDS}, lawyer for bail matters in Supreme Court of India, Supreme Court judgments explained, pro bono legal help India, free legal advice, Hindi legal updates`,
    path: "/",
  },
  updates: {
    title: "Supreme Court Judgment Updates in Simple Words (English & Hindi) | Litigation India",
    description:
      "Read the latest Supreme Court of India judgments explained in plain English and Hindi — short summaries, easy slides and what each ruling means for you, including bail and criminal matters.",
    keywords: `${BASE_KEYWORDS}, Supreme Court judgments, latest Supreme Court verdicts, Supreme Court bail judgments, Supreme Court judgment summary Hindi, Supreme Court case law India`,
    path: "/updates",
  },
  advice: {
    title: "Free Pro Bono Supreme Court Lawyers — Bail & More | Litigation India",
    description:
      "Talk to experienced Supreme Court of India lawyers for free. Get guidance on bail matters, criminal, constitutional, service and family cases from pro bono Supreme Court advocates volunteering their time weekly.",
    keywords: `${BASE_KEYWORDS}, lawyer for bail matters in Supreme Court of India, bail lawyer India, free Supreme Court legal help, pro bono lawyer`,
    path: "/advice",
  },
  team: {
    title: "Our Team of Supreme Court Advocates & Editors | Litigation India",
    description:
      "Meet the experienced Supreme Court of India advocates, editors and volunteers behind Litigation India who make judgments understandable and offer free guidance to litigants.",
    keywords: `${BASE_KEYWORDS}, Supreme Court advocates, legal volunteers`,
    path: "/team",
  },
  about: {
    title: "About Litigation India — Free Supreme Court Legal Guidance for Litigants",
    description:
      "Litigation India is a public-service platform making Supreme Court judgments simple and connecting litigants and junior lawyers with experienced Supreme Court advocates for free guidance.",
    keywords: `${BASE_KEYWORDS}, about Litigation India, free legal help India, pro bono Supreme Court`,
    path: "/about",
  },
  lawyers: {
    title: "SC Lawyers — Supreme Court Advocates India | Litigation India",
    description:
      "Connect with experienced SC lawyers and advocates for SLP, bail, PMLA, FIR quashing, service matters, property, IBC and more. Free initial guidance by volunteer Supreme Court advocates.",
    keywords: `${BASE_KEYWORDS}, SC lawyers, supreme court lawyers, supreme court advocates, SLP lawyer India, bail advocate Supreme Court, PMLA lawyer, quash FIR advocate, service matters CAT appeal, IBC NCLAT appeal, SARFAESI DRT lawyer, ancestral property advocate, cheque bounce Supreme Court, land acquisition compensation lawyer`,
    path: "/supreme-court-lawyers",
  },
  lawyer_sc: {
    title: "Expert Supreme Court Lawyers & Advocates in Delhi | Litigation India",
    description:
      "Looking for an advocate for a Supreme Court appeal or SLP? Access pro bono legal guidance and connect with seasoned Supreme Court lawyers led by Adv. Om Narayan Pandey.",
    keywords: `${BASE_KEYWORDS}, lawyer for supreme court, advocate for supreme court appeal, SLP lawyer, special leave petition advocate, supreme court appeal lawyer Delhi`,
    path: "/lawyer-supreme-court",
  },
  lawyer_hc: {
    title: "High Court Advocates & Litigation Counsel in India | Litigation India",
    description:
      "Connect with dedicated High Court lawyers for property, criminal, civil, and corporate disputes. Free initial consultations and expert guidance available.",
    keywords: `${BASE_KEYWORDS}, lawyer for High Court, High Court advocate, High Court litigation counsel, property lawyer, criminal lawyer, civil appeal advocate`,
    path: "/lawyer-high-court",
  },
  // Practice area pages — SEO data comes directly from practiceAreaContent.js per-page.
  // No static PAGE_SEO entry needed here; each page builds its own title/desc/keywords.
};

export function attorneyJsonLd(lw) {
  if (!lw) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Attorney",
    name: lw.name,
    url: `${SITE_URL}/supreme-court-lawyers/${lw.id}`,
    jobTitle: lw.specialization_en || "Advocate, Supreme Court of India",
    image: lw.photo ? (lw.photo.startsWith("http") ? lw.photo : `${SITE_URL}/api/public/images/${lw.photo}`) : undefined,
    description: lw.bio_en || undefined,
    worksFor: { "@type": "LegalService", name: ORG_NAME, url: SITE_URL },
    areaServed: { "@type": "Country", name: "India" },
    knowsLanguage: lw.languages && lw.languages.length ? lw.languages : undefined,
    knowsAbout: lw.helps_en && lw.helps_en.length ? lw.helps_en : undefined,
    address: lw.location
      ? { "@type": "PostalAddress", addressLocality: lw.location, addressCountry: "IN" }
      : undefined,
  };
}

// Bilingual FAQ used on the Advice page (visible content + FAQPage schema).
export const ADVICE_FAQS = [
  {
    q_en: "Can I get help from a Supreme Court lawyer for a bail matter for free?",
    a_en:
      "Yes. Experienced Supreme Court of India advocates volunteer with Litigation India and offer free pro bono guidance on bail matters. Book a free slot or request a callback and a volunteer advocate will guide you on the next steps.",
    q_hi: "क्या मुझे ज़मानत (बेल) के मामले में सुप्रीम कोर्ट के वकील से मुफ़्त मदद मिल सकती है?",
    a_hi:
      "हाँ। सुप्रीम कोर्ट के अनुभवी अधिवक्ता Litigation India के साथ स्वेच्छा से जुड़े हैं और ज़मानत के मामलों पर निःशुल्क मार्गदर्शन देते हैं। मुफ़्त स्लॉट बुक करें या कॉलबैक का अनुरोध करें।",
  },
  {
    q_en: "How do I get guidance on a bail petition in the Supreme Court of India?",
    a_en:
      "Choose an advocate on this page and book a free slot, or use the 'Request a callback' option. Share a short summary of your case and preferred language (English or Hindi), and a volunteer advocate will explain the process.",
    q_hi: "सुप्रीम कोर्ट में ज़मानत याचिका पर मार्गदर्शन कैसे प्राप्त करूँ?",
    a_hi:
      "इस पृष्ठ पर किसी अधिवक्ता को चुनकर मुफ़्त स्लॉट बुक करें, या 'कॉलबैक का अनुरोध करें' विकल्प का उपयोग करें। अपने मामले का संक्षिप्त विवरण और पसंदीदा भाषा साझा करें।",
  },
  {
    q_en: "Is the legal guidance really free?",
    a_en:
      "Yes. Litigation India is a public-service initiative. The guidance offered here is pro bono (free) and meant to help litigants, ordinary people and junior lawyers understand their options — it does not replace formal engagement of a lawyer.",
    q_hi: "क्या यह कानूनी मार्गदर्शन वाकई मुफ़्त है?",
    a_hi:
      "हाँ। Litigation India एक जनसेवा पहल है। यहाँ दिया गया मार्गदर्शन प्रो बोनो (निःशुल्क) है और इसका उद्देश्य वादकारियों तथा युवा वकीलों को उनके विकल्प समझने में मदद करना है।",
  },
  {
    q_en: "Which types of cases can these Supreme Court advocates help with?",
    a_en:
      "Our volunteer advocates guide on bail matters, criminal law, constitutional law, service matters and family/property disputes connected to the Supreme Court of India, and can point you toward the right process.",
    q_hi: "ये सुप्रीम कोर्ट अधिवक्ता किन प्रकार के मामलों में मदद कर सकते हैं?",
    a_hi:
      "हमारे स्वयंसेवी अधिवक्ता ज़मानत, आपराधिक कानून, संवैधानिक कानून, सेवा मामलों और परिवार/संपत्ति विवादों पर मार्गदर्शन देते हैं।",
  },
  {
    q_en: "How can I request free legal help from Litigation India?",
    a_en:
      "You can request guidance by filling out our secure intake form under the 'Free Legal Help' section. Our volunteer advocates review submissions to help qualify litigants who need it most.",
    q_hi: "मैं Litigation India से निःशुल्क कानूनी मदद कैसे मांग सकता/सकती हूँ?",
    a_hi:
      "'निःशुल्क कानूनी मदद' अनुभाग में हमारा सुरक्षित फ़ॉर्म भरकर मार्गदर्शन का अनुरोध करें। हमारे स्वयंसेवी अधिवक्ता आवेदन की समीक्षा करते हैं।",
  },
  {
    q_en: "Are your Supreme Court judgment summaries available in Hindi?",
    a_en:
      "Yes. Every judgment summary published on our platform is available in both plain English and simple Hindi to help ordinary citizens easily grasp complex legal rulings.",
    q_hi: "क्या आपके सुप्रीम कोर्ट फैसलों के सारांश हिंदी में उपलब्ध हैं?",
    a_hi:
      "हाँ। हमारे मंच पर प्रकाशित हर सारांश सरल अंग्रेज़ी और सरल हिंदी दोनों में उपलब्ध है।",
  },
  {
    q_en: "Does Litigation India represent clients in the Supreme Court?",
    a_en:
      "Litigation India provides initial educational pro bono guidance. If formal court representation or filing is required, users can be connected to qualified, practicing Supreme Court and High Court advocates.",
    q_hi: "क्या Litigation India सुप्रीम कोर्ट में मुवक्किलों का प्रतिनिधित्व करता है?",
    a_hi:
      "Litigation India प्रारंभिक शैक्षिक प्रो बोनो मार्गदर्शन देता है। औपचारिक प्रतिनिधित्व हेतु आपको योग्य अभ्यासरत अधिवक्ताओं से जोड़ा जा सकता है।",
  },
];

// ---------- JSON-LD builders (compliant: factual, no superlative claims) ----------

export function legalServiceJsonLd({ lawyers = [] } = {}) {
  const node = {
    "@context": "https://schema.org",
    "@type": "LegalService",
    "@id": `${SITE_URL}/advice#service`,
    name: "Litigation India — Free Supreme Court Legal Guidance",
    url: `${SITE_URL}/advice`,
    description:
      "Free pro bono guidance from experienced Supreme Court of India advocates on bail matters, criminal, constitutional, service and family cases.",
    areaServed: { "@type": "Country", name: "India" },
    serviceType: PRACTICE_AREAS,
    priceRange: "Free / Pro bono",
    makesOffer: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
      description: "Free pro bono guidance from experienced Supreme Court of India advocates.",
    },
    image: DEFAULT_OG,
  };
  const employees = (lawyers || [])
    .slice(0, 12)
    .filter((lw) => lw && lw.name)
    .map((lw) => {
      const person = {
        "@type": "Attorney",
        name: lw.name,
        jobTitle: lw.specialization_en || "Advocate, Supreme Court of India",
      };
      const knows = Array.isArray(lw.helps_en) ? lw.helps_en.filter(Boolean) : [];
      if (knows.length) person.knowsAbout = knows;
      if (lw.location) person.workLocation = lw.location;
      return person;
    });
  if (employees.length) node.employee = employees;
  return node;
}

export function caseYear(j) {
  if (!j) return "";
  const m = String(j.judgment_date || j.citation || j.published_at || "").match(/\b(19|20)\d{2}\b/);
  return m ? m[0] : "";
}

/** Meta description for a judgment, always under 160 characters.
 * Leads with the direct, plain-language answer, followed by the case ruling
 * summary — so the snippet itself answers the searcher's question. */
export function judgmentMetaDescription(j) {
  const answer = (j.meaning_en || j.one_line_en || "").trim();
  const ruling = (j.decision_en || j.summary_en || "").trim();
  let base;
  if (answer && ruling) {
    base = `${answer} ${ruling}`;
  } else if (answer) {
    base = answer;
  } else if (ruling) {
    base = ruling;
  } else {
    base = `Supreme Court of India ruling in ${j.case_title}.`;
  }
  base = base.replace(/\s+/g, " ").trim();
  if (base.length <= 158) return base;
  const cut = base.slice(0, 155);
  return cut.slice(0, cut.lastIndexOf(" ")) + "…";
}

/** Unique meta title: "[Lead Question of Law] | [Case Name] (Year) | Litigation India". */
export function judgmentMetaTitle(j) {
  const year = caseYear(j);
  const q = (j.headline_question_en || "").trim();
  const caseStr = `${j.case_title}${year ? ` (${year})` : ""}`;
  if (q) {
    // Lead with the question — maximises featured-snippet capture for exact searches
    const qShort = q.length > 90 ? q.slice(0, 87) + "…" : q;
    return `${qShort} | ${caseStr} | Litigation India`;
  }
  return `${caseStr} | Litigation India`;
}

export function articleJsonLd(j) {
  if (!j) return null;
  const slug = j.slug || j.id;
  const desc = judgmentMetaDescription(j);
  const published = j.published_at || j.created_at || undefined;
  const year = caseYear(j);
  const img = j.infographic_image
    ? (j.infographic_image.startsWith("http") ? j.infographic_image : `${SITE_URL}/api/public/images/${j.infographic_image}`)
    : (j.cover_image || DEFAULT_OG);
  const pageUrl = `${SITE_URL}/judgments/${slug}`;

  // Build richer keyword list: case title, year, keywords, provisions, area
  const kwArray = [
    j.case_title,
    year ? `${j.case_title} ${year}` : null,
    ...(Array.isArray(j.keywords) ? j.keywords : []),
    ...((j.relevant_provisions || []).map((p) => (p.sections ? `${p.sections}, ${p.act_name}` : p.act_name)).filter(Boolean)),
    j.area_of_law,
    "Supreme Court of India judgment",
    "Indian Supreme Court case law",
  ].filter(Boolean);

  return {
    "@context": "https://schema.org",
    "@type": ["Article", "LegalDocument"],
    headline: `${j.case_title}${year ? ` (${year})` : ""}`.slice(0, 110),
    alternativeHeadline: j.plain_title_en || j.headline_question_en || undefined,
    description: desc,
    inLanguage: ["en", "hi"],
    datePublished: published,
    dateModified: j.updated_at || published,
    image: img,
    articleSection: j.area_of_law || "Supreme Court of India",
    keywords: kwArray.join(", ") || undefined,
    // LegalDocument-specific fields
    legislationIdentifier: j.citation || undefined,
    legislationDate: j.judgment_date || undefined,
    legislationJurisdiction: { "@type": "AdministrativeArea", name: "India" },
    mentions: (j.relevant_provisions || []).filter((p) => p.act_name).map((p) => ({
      "@type": "Legislation",
      name: p.sections ? `${p.sections}, ${p.act_name}` : p.act_name,
      legislationIdentifier: p.sections || undefined,
      legislationPassedBy: { "@type": "LegislativeBuilding", name: "Parliament of India" },
    })),
    about: {
      "@type": "Legislation",
      name: j.case_title,
      legislationIdentifier: j.citation || undefined,
      legislationDate: j.judgment_date || undefined,
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": pageUrl },
    author: { "@type": "Organization", name: ORG_NAME, url: SITE_URL },
    publisher: {
      "@type": "Organization",
      name: ORG_NAME,
      logo: { "@type": "ImageObject", url: DEFAULT_OG },
    },
    // Speakable spec — enables Google Assistant to read out the key question & answer
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: [
        '[data-testid="judgment-title"]',
        '[data-testid="judgment-ruling-headline"]',
        '[data-testid="judgment-meaning"]',
      ],
    },
  };
}

/** LegalService schema for an individual lawyer profile page. */
export function lawyerLegalServiceJsonLd(lw) {
  if (!lw) return null;
  const url = `${SITE_URL}/supreme-court-lawyers/${lw.id}`;
  const image = lw.photo
    ? (lw.photo.startsWith("http") ? lw.photo : `${SITE_URL}/api/public/images/${lw.photo}`)
    : undefined;
  const services = [
    "Supreme Court of India litigation",
    ...(Array.isArray(lw.helps_en) ? lw.helps_en.filter(Boolean) : []),
    ...(lw.specialization_en ? [lw.specialization_en] : []),
  ];
  return {
    "@context": "https://schema.org",
    "@type": "LegalService",
    "@id": `${url}#legalservice`,
    name: `${lw.name} — Advocate, Supreme Court of India`,
    url,
    image,
    description: lw.bio_en || `${lw.name} is an advocate practising before the Supreme Court of India${lw.specialization_en ? `, focusing on ${lw.specialization_en}` : ""}.`,
    areaServed: lw.location
      ? [{ "@type": "City", name: lw.location }, { "@type": "Country", name: "India" }]
      : { "@type": "Country", name: "India" },
    address: lw.location ? { "@type": "PostalAddress", addressLocality: lw.location, addressCountry: "IN" } : undefined,
    telephone: lw.phone || undefined,
    email: lw.email || undefined,
    priceRange: "Free initial guidance (pro bono)",
    serviceType: Array.from(new Set(services)),
    knowsLanguage: lw.languages && lw.languages.length ? lw.languages : undefined,
    founder: lw.is_founder ? { "@type": "Person", name: lw.name } : undefined,
    employee: {
      "@type": "Attorney",
      name: lw.name,
      jobTitle: lw.specialization_en || "Advocate, Supreme Court of India",
      identifier: lw.bar_council_id ? { "@type": "PropertyValue", propertyID: "Bar Council Enrolment", value: lw.bar_council_id } : undefined,
    },
    parentOrganization: { "@type": "Organization", name: ORG_NAME, url: SITE_URL },
  };
}

/** LegalService schema for a city landing page. */
export function cityLegalServiceJsonLd(cityName, path, lawyers = []) {
  return {
    "@context": "https://schema.org",
    "@type": "LegalService",
    "@id": `${SITE_URL}${path}#legalservice`,
    name: `Our Top Supreme Court Lawyers/Advocates in ${cityName} — Litigation India`,
    url: `${SITE_URL}${path}`,
    description: `Free, pro bono initial guidance from Supreme Court of India advocates for litigants in ${cityName}: appeals, SLPs, bail, constitutional, service and civil matters.`,
    areaServed: [{ "@type": "City", name: cityName }, { "@type": "Country", name: "India" }],
    serviceType: PRACTICE_AREAS,
    priceRange: "Free initial guidance (pro bono)",
    parentOrganization: { "@type": "Organization", name: ORG_NAME, url: SITE_URL },
    employee: (lawyers || []).slice(0, 12).filter((lw) => lw && lw.name).map((lw) => ({
      "@type": "Attorney",
      name: lw.name,
      jobTitle: lw.specialization_en || "Advocate, Supreme Court of India",
      url: `${SITE_URL}/supreme-court-lawyers/${lw.id}`,
    })),
  };
}

// FAQ for the Supreme Court Lawyers directory page (visible + FAQPage schema).
export const SC_LAWYERS_FAQS = [
  {
    q: "How do I find a Supreme Court lawyer in India?",
    a: "Start by identifying the nature of your matter — a criminal appeal, a bail matter, a Special Leave Petition (SLP) against a High Court order, a writ petition under Article 32, or a service or civil dispute. Every advocate listed on this page practises before the Supreme Court of India and lists their practice areas, city, Bar Council enrolment and years of experience. Open a profile, read the biography and book a free initial consultation or request a callback. Our volunteer advocates will explain whether your case has a viable route to the Supreme Court and what the next practical step is.",
  },
  {
    q: "How do I file a case in the Supreme Court of India?",
    a: "Most matters reach the Supreme Court through a Special Leave Petition under Article 136 of the Constitution against a judgment of a High Court or tribunal, which must ordinarily be filed within 90 days of the impugned order. Writ petitions under Article 32 are filed directly for enforcement of fundamental rights, and appeals also lie under specific statutes. The petition must be drafted, settled and filed by an Advocate-on-Record (AoR) registered with the Supreme Court, along with the certified copy of the impugned order, annexures, affidavits and court fee. Our advocates can review your papers and explain the process, limitation and likely costs before you decide.",
  },
  {
    q: "What is the difference between an advocate, an Advocate-on-Record and a Senior Advocate?",
    a: "An advocate is any lawyer enrolled with a State Bar Council under the Advocates Act, 1961. An Advocate-on-Record (AoR) has additionally passed the Supreme Court's AoR examination and is the only category entitled to file and act in matters before the Supreme Court. A Senior Advocate is designated by the Supreme Court or a High Court in recognition of standing and expertise; Senior Counsel argue matters but do not file or interact directly with clients — they are briefed through an AoR.",
  },
  {
    q: "How does the free consultation process work?",
    a: "Choose an advocate and pick an open slot, or use the query form on their profile to request a callback. Share a short, honest summary of your matter and the stage it is at (FIR, trial, High Court order, etc.). A volunteer advocate will speak with you by phone or WhatsApp, explain your legal position in plain English or Hindi, and tell you what documents to gather. This initial guidance is free and educational; it does not create an attorney-client relationship or replace a formally engaged lawyer.",
  },
  {
    q: "Can a Supreme Court lawyer help with a bail matter?",
    a: "Yes. Bail is one of the most common categories of matters before the Supreme Court — regular bail, anticipatory bail under Section 482 of the Bharatiya Nagarik Suraksha Sanhita, 2023 (formerly Section 438 CrPC), and default bail under Section 187 BNSS (formerly Section 167(2) CrPC). Where a High Court has refused bail, an SLP can be filed in the Supreme Court. Our advocates regularly guide families on the correct forum, timing and documents for bail applications.",
  },
  {
    q: "How much does a Supreme Court lawyer charge in India?",
    a: "Fees vary widely with the seniority of counsel, the complexity of the matter and the number of hearings. The initial guidance provided through Litigation India is free (pro bono). If your matter requires formal filing or representation, the advocate will explain the likely fee structure — drafting and filing charges, per-appearance fees and court fee — transparently before any engagement.",
  },
  {
    q: "Do I have to be in Delhi to consult a Supreme Court lawyer?",
    a: "No. The Supreme Court sits in New Delhi, but initial guidance is provided remotely by phone or WhatsApp from any city — Mumbai, Chennai, Bangalore, Hyderabad, Kanpur, Pune, Ahmedabad, Gurugram, Chandigarh or elsewhere. Many hearings are also conducted through the Court's hybrid video-conferencing facility, and your local advocate can coordinate with an Advocate-on-Record in Delhi.",
  },
  {
    q: "What happens after a Special Leave Petition is filed?",
    a: "After scrutiny by the Registry and curing of defects, the SLP is listed before a Bench for admission. The Court may dismiss it at the threshold, issue notice to the other side, or grant leave and convert it into a civil or criminal appeal. Interim relief — such as a stay of the High Court order or protection from arrest — can be sought at the notice stage. Final hearing follows once pleadings are complete.",
  },
];

export function scLawyersFaqJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: SC_LAWYERS_FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function breadcrumbJsonLd(items = []) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

/**
 * WebPage schema for practice area pages — helps Google understand
 * the page type, author and last reviewed date.
 */
export function practiceAreaWebPageJsonLd({ area, path }) {
  const url = `${SITE_URL}${path}`;
  const today = new Date().toISOString().slice(0, 10);
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: area.metaTitle,
    description: area.metaDescription,
    inLanguage: ["en-IN", "hi-IN"],
    isPartOf: { "@type": "WebSite", "@id": `${SITE_URL}/#website`, url: SITE_URL, name: ORG_NAME },
    about: { "@type": "LegalService", "@id": `${url}#legalservice` },
    keywords: area.keywords,
    datePublished: "2024-01-01",
    dateModified: today,
    author: { "@type": "Organization", name: ORG_NAME, url: SITE_URL },
    publisher: {
      "@type": "Organization",
      name: ORG_NAME,
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: DEFAULT_OG, width: 1200, height: 630 },
    },
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["h1", '[data-testid="section-what"]', '[data-testid="section-faqs"]'],
    },
  };
}

/**
 * HowTo schema for the process steps section on practice area pages.
 * Enables Google's rich result for "how to file [X] in Supreme Court".
 */
export function howToJsonLd({ area, path }) {
  const url = `${SITE_URL}${path}`;
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "@id": `${url}#howto`,
    name: area.process.title,
    description: `Step-by-step guide: ${area.process.title} — ${area.shortTitle} in the Supreme Court of India.`,
    image: DEFAULT_OG,
    inLanguage: "en-IN",
    tool: [
      { "@type": "HowToTool", name: "Certified copy of impugned order" },
      { "@type": "HowToTool", name: "Advocate on Record (AoR)" },
    ],
    step: area.process.steps.map((step, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: step.title,
      text: step.desc,
      url: `${url}#step-${i + 1}`,
    })),
  };
}

/**
 * Organization schema — included on practice area pages to associate them
 * with the organisation, aiding entity recognition.
 */
export function orgJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": ["Organization", "LegalService"],
    "@id": `${SITE_URL}/#organization`,
    name: ORG_NAME,
    url: SITE_URL,
    logo: {
      "@type": "ImageObject",
      url: `${SITE_URL}/li-logo.svg`,
      width: 240,
      height: 72,
    },
    description:
      "Litigation India explains recent Supreme Court judgments in plain English & Hindi and connects litigants with volunteer Supreme Court advocates for free initial guidance.",
    areaServed: { "@type": "Country", name: "India" },
    serviceType: PRACTICE_AREAS,
    sameAs: [SITE_URL],
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+9107290071137",
      contactType: "legal guidance",
      areaServed: "IN",
      availableLanguage: ["en", "hi"],
    },
  };
}

export function faqJsonLd(faqs = ADVICE_FAQS) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q_en,
      acceptedAnswer: { "@type": "Answer", text: f.a_en },
    })),
  };
}

export function itemListJsonLd(items = [], basePath = "/judgments/") {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: items.slice(0, 20).map((j, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE_URL}${basePath}${j.slug || j.id}`,
      name: j.case_title,
    })),
  };
}
